import { Button } from "@studyplus/boron-ui";
import classNames from "classnames";
import * as React from "react";
import Icon from "../../../components/atoms/Icon";
import Input from "../../../components/atoms/Input";
import Loader from "../../../components/atoms/Loader";
import { FilterWrapper } from "../../../components/features/FilterWrapper";
import { StudentTagFilter } from "../../../components/features/NewStudentFilter/StudentTagFilter";
import { Text } from "../../../components/general/Text";
import type StudentTag from "../../../domains/StudentTag";
import { useFetchStudentTags } from "../../../hooks/http/useFetchStudentTags";
import { useScrollWatcher } from "../../../hooks/useScrollWatcher";
import {
  NO_STUDENT_FOUND_MESSAGE,
  STUDENT_SEARCH_FAILED_MESSAGE,
  toUnselectableReasonLabel,
} from "./messages";
import {
  hasStudentSearchCondition,
  type IssuableStudent,
  type StudentSearchConditions,
  toIssuableStudentLabel,
} from "./types";
import { useSearchIssuableStudents } from "./useSearchIssuableStudents";

type Props = {
  sectionId: string;
  selectedStudent: IssuableStudent | null;
  onSelect: (student: IssuableStudent | null) => void;
};

// 起票する生徒を探して 1 人選ぶ。姓・名と、生徒一覧と同じ絞込検索（学年・校舎のタグ・スマートタグ）で探す
export const StudentSearch = ({
  sectionId,
  selectedStudent,
  onSelect,
}: Props) => {
  const [lastName, setLastName] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [selectedTags, setSelectedTags] = React.useState<StudentTag[]>([]);
  // 検索した時点の条件。入力欄を書き換えただけでは検索し直さない
  const [conditions, setConditions] =
    React.useState<StudentSearchConditions | null>(null);
  // 同じ条件で押し直したとき（失敗後の再試行や、契約状態の更新を見たいとき）も検索し直すために数える
  const [searchCount, setSearchCount] = React.useState(0);

  const tags = useSelectableStudentTags(sectionId);
  const { students, isPending, isFetchingNextPage, error, loadMore } =
    useSearchIssuableStudents({ sectionId, conditions, searchCount });
  const handleScroll = useScrollWatcher({ onReachToBottom: loadMore });

  // 検索し直したら選択を外す
  const search = (nextTags: StudentTag[]) => {
    setConditions(
      buildConditions({ lastName, firstName, selectedTags: nextTags }),
    );
    setSearchCount((count) => count + 1);
    onSelect(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    search(selectedTags);
  };

  const handleSelectTag = (tag: StudentTag) => () => {
    const nextTags = [...selectedTags, tag];
    setSelectedTags(nextTags);
    search(nextTags);
  };

  const handleDeselectTag = (tag: StudentTag) => () => {
    const nextTags = selectedTags.filter(({ id }) => id !== tag.id);
    setSelectedTags(nextTags);
    search(nextTags);
  };

  const canSearch = hasStudentSearchCondition(
    buildConditions({ lastName, firstName, selectedTags }),
  );

  const renderResults = () => {
    if (!conditions || !hasStudentSearchCondition(conditions)) {
      return null;
    }
    if (isPending) {
      return <Loader loading />;
    }
    if (error || !students) {
      return <Text color="red">{STUDENT_SEARCH_FAILED_MESSAGE}</Text>;
    }
    if (students.length === 0) {
      return <Text>{NO_STUDENT_FOUND_MESSAGE}</Text>;
    }
    return (
      <ul
        aria-label="検索結果"
        className="m-0 max-h-[24rem] list-none overflow-y-auto rounded border border-solid border-gray-400 p-0"
        onScroll={handleScroll}
      >
        {students.map((student) => (
          <li key={student.id}>
            <StudentCandidate
              student={student}
              onClick={() => onSelect(student)}
            />
          </li>
        ))}
        {isFetchingNextPage && (
          <li>
            <Loader loading />
          </li>
        )}
      </ul>
    );
  };

  return (
    <div>
      <form className="flex gap-4" onSubmit={handleSubmit}>
        <Input
          aria-label="姓"
          placeholder="姓"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          aria-label="名"
          placeholder="名"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Button type="submit" disabled={!canSearch} className="shrink-0">
          検索
        </Button>
      </form>
      <FilterWrapper
        StudentTagFilter={
          <StudentTagFilter
            tags={tags}
            selectedTags={selectedTags}
            onSelect={handleSelectTag}
            onDeselect={handleDeselectTag}
          />
        }
      />
      {selectedStudent ? (
        <SelectedStudent
          student={selectedStudent}
          onClear={() => onSelect(null)}
        />
      ) : (
        renderResults()
      )}
    </div>
  );
};

const StudentCandidate = ({
  student,
  onClick,
}: {
  student: IssuableStudent;
  onClick: () => void;
}) => (
  // 選べない生徒も出し、理由を添える（ISS-03）。既に依頼がある生徒は「発行済み」を添えて選べるままにする（ISS-04）
  <button
    type="button"
    disabled={!student.selectable}
    onClick={onClick}
    className={classNames(
      "flex w-full items-center gap-4 border-0 bg-white px-6 py-4 text-left",
      student.selectable
        ? "cursor-pointer text-black hover:bg-gray-100"
        : "cursor-not-allowed text-gray-600",
    )}
  >
    <span className="flex-1">{toIssuableStudentLabel(student)}</span>
    {student.hasExistingRequest && <span>発行済み</span>}
    {!student.selectable && (
      <span className="text-sm">
        {toUnselectableReasonLabel(student.unselectableReason)}
      </span>
    )}
  </button>
);

const SelectedStudent = ({
  student,
  onClear,
}: {
  student: IssuableStudent;
  onClear: () => void;
}) => (
  <div className="flex items-center gap-6 rounded border border-solid border-blue-300 bg-blue-100 px-6 py-4">
    <span className="text-sm text-gray-600">選択中の生徒</span>
    <span className="flex-1 font-bold">{toIssuableStudentLabel(student)}</span>
    {/* モーダル右上の閉じる操作と区別するため、生徒名のすぐ横に置く */}
    <button
      type="button"
      aria-label="選択を外す"
      onClick={onClear}
      className="cursor-pointer border-0 bg-transparent p-0"
    >
      <Icon name="icon-close-x" />
    </button>
  </div>
);

const buildConditions = ({
  lastName,
  firstName,
  selectedTags,
}: {
  lastName: string;
  firstName: string;
  selectedTags: StudentTag[];
}): StudentSearchConditions => {
  const idsOf = (type: StudentTag["type"]) =>
    selectedTags.filter((tag) => tag.type === type).map(({ id }) => id);
  return {
    lastName,
    firstName,
    job: idsOf("job"),
    tagIds: idsOf("tag_ids"),
    smartTag: idsOf("smart_tag"),
  };
};

// 生徒一覧の絞込検索と同じ並び（学年 → 校舎のタグ → 有効なスマートタグ。タグは名前順）。
// 起票用の生徒検索が受け付けない連携状態のタグは出さない
const useSelectableStudentTags = (sectionId: string): StudentTag[] => {
  const { data } = useFetchStudentTags({ sectionId });
  return React.useMemo(() => {
    const tags = data?.tags ?? [];
    const byName = (a: StudentTag, b: StudentTag) => (a.name > b.name ? 1 : -1);
    return [
      ...tags.filter(({ type }) => type === "job"),
      ...tags.filter(({ type }) => type === "tag_ids").sort(byName),
      ...tags
        .filter(({ type, enabled }) => type === "smart_tag" && enabled)
        .sort(byName),
    ];
  }, [data]);
};
